import { z, type ZodRawShape, type ZodObject, type ZodIssue } from 'zod';
import { createEnvClient, type EnvClient } from './client.js';
import { EnvError } from './errors.js';
import { testResets } from './testing-state.js';

export interface DefinedEnv<S extends ZodRawShape> extends EnvClient<z.output<ZodObject<S>>> {
  schema: ZodObject<S>;
  parse: (raw: unknown) => z.output<ZodObject<S>>;
}

const unsafeKeys = new Set(['__proto__', 'constructor', 'prototype']);
const issueCodes = new Set<string>(Object.values(z.ZodIssueCode));
const expectedTypes = new Set<string>(Object.values(z.ZodParsedType));

function assertSafeKeys(value: unknown, seen = new WeakSet<object>()): void {
  if (!value || typeof value !== 'object') return;
  if (seen.has(value)) throw new EnvError('validation', 'config must not contain cycles');
  seen.add(value);
  for (const key of Object.keys(value)) {
    if (unsafeKeys.has(key)) throw new EnvError('validation', 'config contains an unsafe key');
    assertSafeKeys((value as Record<string, unknown>)[key], seen);
  }
  seen.delete(value);
}

export function defineEnv<S extends ZodRawShape>(shape: S): DefinedEnv<S> {
  if (Object.keys(shape).some((key) => unsafeKeys.has(key))) {
    throw new EnvError('validation', 'schema contains an unsafe key');
  }
  const schema = z.object(shape);
  function parse(raw: unknown): z.output<ZodObject<S>> {
    assertSafeKeys(raw);
    let result: ReturnType<typeof schema.safeParse>;
    try {
      result = schema.safeParse(raw);
    } catch {
      throw new EnvError(
        'validation',
        'env-config.json validation failed: invalid input or schema callback',
      );
    }
    if (!result.success) {
      // Record keys and custom issue paths can contain input values. Show only a declared
      // root field, never nested paths. Literal expectations and arbitrary issue metadata
      // are payloads too; only invalid_type may display a canonical Zod type name.
      const issues = result.error.issues
        .map((issue: ZodIssue) => {
          const rootKey = issue.path[0];
          const key =
            typeof rootKey === 'string' && Object.prototype.hasOwnProperty.call(shape, rootKey)
              ? rootKey
              : '<root>';
          const code = issueCodes.has(issue.code) ? issue.code : 'custom';
          const expected =
            issue.code === 'invalid_type' &&
            typeof issue.expected === 'string' &&
            expectedTypes.has(issue.expected)
              ? issue.expected
              : code;
          return `  - ${key}: expected ${expected} (${code})`;
        })
        .join('\n');
      throw new EnvError('validation', `env-config.json validation failed:\n${issues}`);
    }
    return result.data;
  }
  const client = createEnvClient(parse);
  const handle = { schema, parse, ...client };
  testResets.set(handle, testResets.get(client)!);
  return handle;
}
